import { useParams, Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import DOMPurify from "dompurify";
import { Navigation } from "@/components/Navigation";
import { Footer } from "@/components/Footer";
import { SEO } from "@/components/SEO";
import { Breadcrumbs } from "@/components/Breadcrumbs";
import { articleSchema } from "@/lib/structuredData";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Calendar, Clock, ArrowLeft, Share2, Twitter, Facebook, Linkedin, Copy, Youtube } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";

export default function BlogPost() {
  const { slug } = useParams();
  const { toast } = useToast();

  // Fetch blog post by slug
  const { data: post, isLoading } = useQuery({
    queryKey: ['blog-post', slug],
    queryFn: async () => {
      if (!slug) return null;
      
      const { data, error } = await supabase
        .from('blog_posts')
        .select('*')
        .eq('slug', slug)
        .eq('status', 'published')
        .single();
      
      if (error) {
        console.error('Error fetching blog post:', error);
        return null;
      }
      
      return data;
    },
    enabled: !!slug,
  });

  const shareUrl = typeof window !== 'undefined' ? window.location.href : '';
  const shareTitle = post?.title || '';

  const handleCopyLink = () => {
    navigator.clipboard.writeText(shareUrl);
    toast({
      title: "Link copied!",
      description: "The link has been copied to your clipboard.",
    });
  };

  const handleShare = (platform: string) => {
    let url = '';
    switch (platform) {
      case 'twitter':
        url = `https://twitter.com/intent/tweet?text=${encodeURIComponent(shareTitle)}&url=${encodeURIComponent(shareUrl)}`;
        break;
      case 'facebook':
        url = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`;
        break;
      case 'linkedin':
        url = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareUrl)}`;
        break;
    }
    if (url) {
      window.open(url, '_blank', 'noopener,noreferrer,width=600,height=400');
    }
  };

  // Format content for display (simple markdown to HTML conversion)
  const formatContent = (content: string) => {
    return content
      .replace(/## (.*)/g, '<h2 class="font-display text-2xl font-bold mt-8 mb-4 text-foreground">$1</h2>')
      .replace(/\*\*(.*?)\*\*/g, '<strong class="font-bold text-foreground">$1</strong>')
      .replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2" target="_blank" rel="noopener noreferrer" class="text-accent underline hover:no-underline font-medium">$1<span class="sr-only"> (opens in a new tab)</span></a>')
      .replace(/\n\n/g, '</p><p class="mb-4 text-foreground/90 leading-relaxed">')
      .replace(/^(.+)$/gm, '<p class="mb-4 text-foreground/90 leading-relaxed">$1</p>');
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background">
        <Navigation />
        <div className="container mx-auto px-4 py-32 text-center">
          <p className="text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }

  if (!post) {
    return (
      <div className="min-h-screen bg-background">
        <Navigation />
        <div className="container mx-auto px-4 py-32 text-center">
          <h1 className="font-display text-4xl font-bold text-foreground mb-4">
            Post Not Found
          </h1>
          <p className="text-muted-foreground mb-8">
            The blog post you're looking for doesn't exist or has been removed.
          </p>
          <Button asChild>
            <Link to="/blog">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Blog
            </Link>
          </Button>
        </div>
      </div>
    );
  }

  const categoryKeywords: Record<string, string> = {
    "Raw Truth Talks": "raw truth fatherhood, honest dad talk, authentic masculinity",
    "Brotherhood Conversations": "brotherhood fathers, men supporting men, dad community",
    "Story-Driven Lessons": "father stories, dad journey, parenting lessons",
  };

  const postKeywords = `${categoryKeywords[post.category] || ""}, fatherhood, authentic masculinity, men's mental health, present parenting, dad blog`;

  return (
    <div className="min-h-screen bg-background">
      <SEO
        title={post.title}
        description={post.excerpt.substring(0, 155)}
        keywords={postKeywords}
        type="article"
        url={`/blog/${post.slug}`}
        image={post.image_url || "/og-image-new.jpg"}
        publishedTime={post.published_at || undefined}
        schema={articleSchema({
          title: post.title,
          description: post.excerpt,
          author: "Rob Lasa",
          datePublished: post.published_at || post.created_at,
          dateModified: post.published_at || post.created_at,
          image: post.image_url || "https://souldadrob.com/og-image-new.jpg",
          url: `https://souldadrob.com/blog/${post.slug}`,
        })}
      />
      <Navigation />
      <Breadcrumbs />
      
      {/* Hero Image */}
      <div className="relative h-[60vh] min-h-[400px] bg-secondary overflow-hidden">
        {post.image_url && (
          <img
            src={post.image_url}
            alt={post.title}
            className="w-full h-full object-cover"
          />
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-background via-background/50 to-transparent" />
        
        {/* Back Button */}
        <div className="absolute top-8 left-4 md:left-8 z-10">
          <Button asChild variant="ghost" className="text-primary-foreground hover:bg-primary-foreground/10">
            <Link to="/blog">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Blog
            </Link>
          </Button>
        </div>
      </div>

      {/* Article Content */}
      <article className="container mx-auto px-4 -mt-32 relative z-10 max-w-4xl">
        <div className="bg-background rounded-lg shadow-bold p-8 md:p-12">
          {/* Header */}
          <header className="mb-8 space-y-4">
            <Badge className="bg-accent text-accent-foreground">{post.category}</Badge>
            
            <h1 className="font-display text-4xl md:text-5xl font-black text-foreground leading-tight">
              {post.title}
            </h1>
            
            <p className="text-xl text-muted-foreground leading-relaxed">
              {post.excerpt}
            </p>
            
            <div className="flex items-center gap-4 text-sm text-muted-foreground pt-4 border-t border-border">
              <div className="flex items-center gap-2">
                <Calendar className="w-4 h-4" />
                <span>{new Date(post.published_at).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}</span>
              </div>
              <span>•</span>
              <div className="flex items-center gap-2">
                <Clock className="w-4 h-4" />
                <span>{post.read_time}</span>
              </div>
              <div className="ml-auto">
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="outline" size="sm">
                      <Share2 className="w-4 h-4 mr-2" />
                      Share
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuItem onClick={() => handleShare('twitter')}>
                      <Twitter className="w-4 h-4 mr-2" />
                      Share on X
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => handleShare('facebook')}>
                      <Facebook className="w-4 h-4 mr-2" />
                      Share on Facebook
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => handleShare('linkedin')}>
                      <Linkedin className="w-4 h-4 mr-2" />
                      Share on LinkedIn
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={handleCopyLink}>
                      <Copy className="w-4 h-4 mr-2" />
                      Copy Link
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            </div>
          </header>

          {/* Body */}
          <div 
            className="prose prose-lg max-w-none"
            dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(formatContent(post.content)) }}
          />

          {/* Signature */}
          <div className="mt-12 pt-8 border-t border-border">
            <p className="text-muted-foreground italic">
              — Rob
            </p>
            <p className="text-sm text-muted-foreground mt-2">
              You don't have to be perfect. Just be real.
            </p>
          </div>
        </div>

        {/* Call to Action */}
        <div className="mt-16 bg-gradient-hero rounded-lg p-8 md:p-12 text-center">
          <h2 className="font-display text-3xl font-bold text-primary-foreground mb-4">
            Ready to Wake Up?
          </h2>
          <p className="text-xl text-primary-foreground/90 mb-8 max-w-2xl mx-auto">
            Take the Nice Guy Assessment and discover what's keeping you buried.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button asChild size="lg" className="bg-background text-foreground hover:bg-background/90">
              <Link to="/nice-guy-assessment">
                Take the Assessment
              </Link>
            </Button>
            <Button 
              asChild 
              size="lg" 
              variant="outline" 
              className="border-primary-foreground/20 text-primary-foreground hover:bg-primary-foreground/10"
            >
              <a 
                href="https://www.youtube.com/@souldadrob" 
                target="_blank" 
                rel="noopener noreferrer"
              >
                <Youtube className="mr-2 h-5 w-5" />
                Subscribe
                <span className="sr-only"> (opens in a new tab)</span>
              </a>
            </Button>
          </div>
        </div>
      </article>

      <Footer />
    </div>
  );
}